import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  ShieldCheck,
  ShieldOff,
  ShieldAlert,
  KeyRound,
  CheckCircle2,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { adminApi, type AdminUserDetail } from '@/services/adminApi';
import { AgencyUpgradeLinkCard } from '@/components/admin/AgencyUpgradeLinkCard';
import { useAuth } from '@/contexts/AuthContext';

/**
 * AdminUserDetailPage — single user deep-dive for super admins.
 *
 * Three sections:
 *   1. Identity card with Edit drawer (display name, accountType, agencyPlanId, ...)
 *   2. Memberships (clickable to customer detail)
 *   3. Security: reset password, verify email, last sign-in
 *   4. Danger zone: disable / enable / impersonate (read-only)
 *
 * Mutations require a non-empty `reason` enforced by the BE
 * `requireReason` middleware. Self-disable / self-impersonate are
 * blocked by the BE; we also disable the buttons here for clarity.
 */
export default function AdminUserDetailPage() {
  const { uid = '' } = useParams<{ uid: string }>();
  const { user: currentUser } = useAuth();
  const isSelf = currentUser?.uid === uid;

  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const detail = await adminApi.users.detail(uid);
        if (!cancelled) setData(detail);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, refreshTick]);

  const refresh = () => setRefreshTick((t) => t + 1);

  // Action dialog open state — single source of truth per dialog
  const [editOpen, setEditOpen] = useState(false);
  const [pendingDisable, setPendingDisable] = useState(false);
  const [pendingEnable, setPendingEnable] = useState(false);
  const [pendingResetPwd, setPendingResetPwd] = useState(false);
  const [pendingVerify, setPendingVerify] = useState(false);
  const [pendingImpersonate, setPendingImpersonate] = useState(false);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to="/admin/users"><ArrowLeft className="h-4 w-4" /> Back to users</Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-md bg-primary/10 flex items-center justify-center text-xl font-semibold text-primary overflow-hidden">
          {data.avatarUrl ? (
            <img src={data.avatarUrl} alt={data.displayName} className="h-full w-full object-cover" />
          ) : (
            (data.displayName || data.email || '?')[0]?.toUpperCase()
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {data.displayName || '(no name)'}
            {data.auth?.customClaims && (data.auth.customClaims as { sudo?: unknown }).sudo && (
              <Badge variant="destructive" className="gap-1">
                <ShieldCheck className="h-3 w-3" /> Super admin
              </Badge>
            )}
            {data.auth?.disabled && <Badge variant="outline">Disabled</Badge>}
            {isSelf && <Badge variant="secondary" className="text-xs">You</Badge>}
          </h1>
          <p className="text-sm text-muted-foreground">{data.email}</p>
          <p className="text-xs text-muted-foreground font-mono mt-1">{data.uid}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPendingImpersonate(true)}
          disabled={isSelf}
          title={isSelf ? "You can't impersonate your own account" : 'Impersonate (read-only)'}
          className="gap-1.5"
        >
          <Eye className="h-3.5 w-3.5" />
          Impersonate
        </Button>
      </div>

      {/* Identity */}
      <Card className="border-border/50">
        <CardContent className="p-5 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Identity
            </h2>
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>Edit</Button>
          </div>
          <Row k="Display name" v={data.displayName || '—'} />
          <Row k="Account type" v={data.accountType ?? '—'} />
          {data.agencyPlanId && <Row k="Agency plan" v={data.agencyPlanId} />}
          {data.agencyBillingPeriod && <Row k="Billing period" v={data.agencyBillingPeriod} />}
          <Row k="Locale" v={data.locale ?? '—'} />
          <Row k="Timezone" v={data.timezone ?? '—'} />
        </CardContent>
      </Card>

      <AgencyUpgradeLinkCard uid={data.uid} userEmail={data.email} />

      {/* Memberships */}
      <Card className="border-border/50">
        <CardContent className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Memberships ({data.membershipsCount})
          </h2>
          {data.membershipsCount === 0 ? (
            <p className="text-sm text-muted-foreground">No company memberships.</p>
          ) : (
            <ul className="space-y-1.5">
              {Object.entries(data.memberships ?? {}).map(([cid, m]) => {
                const mb = m as { companyName?: string; permission?: string };
                return (
                  <li key={cid} className="text-sm flex items-center gap-3">
                    <Link to={`/admin/customers/${cid}`} className="font-medium hover:underline">
                      {mb.companyName ?? cid}
                    </Link>
                    {mb.permission && (
                      <Badge variant="outline" className="text-xs">{mb.permission}</Badge>
                    )}
                    <span className="text-xs text-muted-foreground font-mono ml-auto">{cid}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="border-border/50">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Security
          </h2>
          <Row
            k="Email"
            v={data.email ? (
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                {data.email}
                {data.auth?.emailVerified ? (
                  <Badge variant="secondary" className="text-xs gap-1"><CheckCircle2 className="h-3 w-3" /> Verified</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">Unverified</Badge>
                )}
              </span>
            ) : '—'}
          />
          {data.auth?.phoneNumber && (
            <Row k="Phone" v={
              <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {data.auth.phoneNumber}</span>
            } />
          )}
          {data.auth?.providers && data.auth.providers.length > 0 && (
            <Row k="Providers" v={data.auth.providers.join(', ')} />
          )}
          <Row k="Last sign-in" v={data.auth?.lastSignInAt ? new Date(data.auth.lastSignInAt).toLocaleString('en-GB') : '—'} />
          <Row k="Account created" v={data.auth?.authCreatedAt ? new Date(data.auth.authCreatedAt).toLocaleString('en-GB') : '—'} />
          <div className="flex flex-wrap gap-2 pt-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPendingResetPwd(true)}>
              <KeyRound className="h-3.5 w-3.5" /> Send password reset
            </Button>
            {!data.auth?.emailVerified && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPendingVerify(true)}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Force email verified
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="border-destructive/30">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-destructive">
            Danger zone
          </h2>
          {data.auth?.disabled ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setPendingEnable(true)}
              disabled={isSelf}
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Enable account
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => setPendingDisable(true)}
              disabled={isSelf}
              title={isSelf ? "You can't disable your own account" : ''}
            >
              <ShieldOff className="h-3.5 w-3.5" /> Disable account
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ─── Dialogs ──────────────────────────────────────────────────────── */}
      <EditUserDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        user={data}
        onSaved={() => { setEditOpen(false); refresh(); }}
      />
      <ReasonConfirmDialog
        open={pendingDisable}
        onOpenChange={setPendingDisable}
        title="Disable account?"
        description={`This blocks ${data.email} from signing in. Existing sessions are revoked.`}
        confirmLabel="Disable"
        destructive
        onConfirm={async (reason) => {
          await adminApi.users.disable(uid, reason);
          toast.success(`Account ${data.email} disabled.`);
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingEnable}
        onOpenChange={setPendingEnable}
        title="Enable account?"
        description={`This re-enables ${data.email}. They can sign in again immediately.`}
        confirmLabel="Enable"
        onConfirm={async (reason) => {
          await adminApi.users.enable(uid, reason);
          toast.success(`Account ${data.email} enabled.`);
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingResetPwd}
        onOpenChange={setPendingResetPwd}
        title="Send password reset email?"
        description={`A reset link will be sent to ${data.email}. The link is also returned on screen so you can copy it manually.`}
        confirmLabel="Send"
        onConfirm={async (reason) => {
          const r = await adminApi.users.resetPassword(uid, reason);
          toast.success(`Reset email queued to ${r.sentTo}`, {
            description: 'Manual link copied to clipboard if available.',
          });
          try {
            await navigator.clipboard.writeText(r.resetLink);
          } catch {
            // ignore clipboard errors
          }
        }}
      />
      <ReasonConfirmDialog
        open={pendingVerify}
        onOpenChange={setPendingVerify}
        title="Force email verified?"
        description={`This bypasses the normal verification flow. Use only when the user can't access their inbox.`}
        confirmLabel="Mark verified"
        onConfirm={async (reason) => {
          await adminApi.users.verifyEmail(uid, reason);
          toast.success(`${data.email} marked as verified.`);
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingImpersonate}
        onOpenChange={setPendingImpersonate}
        title="Impersonate this user (read-only)?"
        description={`You'll get a 30-minute read-only token to view the app as ${data.email}. All write attempts will be blocked.`}
        confirmLabel="Get impersonation token"
        destructive
        onConfirm={async (reason) => {
          const r = await adminApi.users.impersonate(uid, reason);
          await navigator.clipboard.writeText(r.customToken).catch(() => undefined);
          toast.success('Impersonation token copied to clipboard', {
            description: `Use signInWithCustomToken(auth, '<paste>'). Expires at ${new Date(r.expiresAt).toLocaleTimeString('en-GB')}.`,
            duration: 10000,
          });
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-muted-foreground w-32 shrink-0">{k}</span>
      <span className="font-medium break-all">{v}</span>
    </div>
  );
}

function EditUserDialog({
  open,
  onOpenChange,
  user,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  user: AdminUserDetail;
  onSaved: () => void;
}) {
  const initial = useMemo(() => ({
    displayName: user.displayName ?? '',
    accountType: user.accountType ?? '',
    agencyPlanId: user.agencyPlanId ?? '',
    agencyBillingPeriod: user.agencyBillingPeriod ?? '',
    locale: user.locale ?? '',
    timezone: user.timezone ?? '',
  }), [user]);
  const [form, setForm] = useState(initial);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(initial); setReason(''); }, [initial, open]);

  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const diff: Record<string, unknown> = { reason };
      for (const [k, v] of Object.entries(form)) {
        if (v !== (initial as Record<string, string>)[k]) {
          diff[k] = v;
        }
      }
      if (Object.keys(diff).length === 1) {
        toast.info('No changes to save.');
        return;
      }
      const res = await adminApi.users.update(user.uid, diff as Parameters<typeof adminApi.users.update>[1]) as {
        warnings?: string[];
      };
      if (res?.warnings?.length) {
        toast.warning(`User updated with billing warnings: ${res.warnings.join(' ')}`);
      } else {
        toast.success('User updated');
      }
      onSaved();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>
            Updating {user.email}. The user will see the changes after their next sign-in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="displayName">Display name</Label>
            <Input id="displayName" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="accountType">Account type</Label>
            <Select value={form.accountType || 'individual'} onValueChange={(v) => setForm({ ...form, accountType: v })}>
              <SelectTrigger id="accountType"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual (SME)</SelectItem>
                <SelectItem value="agency_owner">Agency owner</SelectItem>
                <SelectItem value="invited_member">Invited member</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Identity-only: does not charge Stripe. Use the Agency upgrade link card below to send checkout.
              Demotion to individual is blocked here.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="agencyPlanId">Agency plan</Label>
              <Select
                value={form.agencyPlanId || ''}
                onValueChange={(v) => setForm({ ...form, agencyPlanId: v })}
                disabled={form.accountType !== 'agency_owner'}
              >
                <SelectTrigger id="agencyPlanId"><SelectValue placeholder="Required for agency owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agency_starter">agency_starter</SelectItem>
                  <SelectItem value="agency_medium">agency_medium</SelectItem>
                  <SelectItem value="agency_professional">agency_professional</SelectItem>
                  <SelectItem value="agency_enterprise">agency_enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agencyBillingPeriod">Period</Label>
              <Select value={form.agencyBillingPeriod} onValueChange={(v) => setForm({ ...form, agencyBillingPeriod: v })}>
                <SelectTrigger id="agencyBillingPeriod"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="locale">Locale</Label>
              <Input id="locale" value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })} placeholder="en-US" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="timezone">Timezone</Label>
              <Input id="timezone" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="Europe/Rome" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you making this change?" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()}>
            {busy ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Generic destructive confirm dialog with a required `reason` textarea.
 * Reused for disable / enable / reset / verify / impersonate.
 */
function ReasonConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {destructive ? <ShieldAlert className="h-4 w-4 text-destructive" /> : <ShieldCheck className="h-4 w-4" />}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="reason-confirm">Reason <span className="text-destructive">*</span></Label>
          <Textarea
            id="reason-confirm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you doing this?"
            rows={2}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={busy || !reason.trim()}
            className={destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {busy ? 'Working...' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
